import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileUp,
  ImagePlus,
  Loader2,
  Plus,
  Send,
  Trash2,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import CoverImage from '@/components/blog/CoverImage'
import ImageAltText from '@/components/blog/ImageAltText'
import ImportInstructions from '@/components/blog/ImportInstructions'
import PreviewMarkdown, { countAuthoringComments } from '@/components/blog/PreviewMarkdown'
import {
  ACCEPT_IMAGES,
  CONTENT_WIDTH_PX,
  ImageReplaceError,
  altFromFilename,
  buildCover,
  buildReplacement,
  buildUpload,
  caretFromPoint,
  describe,
  imageFilesFrom,
  insertImageMarkdown,
  isFileDrag,
  referencedImages,
  replacementNeedsBodyRewrite,
  setImageAlt,
} from '@/components/blog/draftImages'
import { api } from '@/services/api'
import {
  BLOG_CATEGORIES,
  blogApi,
  messageOf,
  type BlogCategory,
  type DraftImage,
  type FaqItem,
  type PostDraft,
  type ValidationResult,
} from '@/services/blogApi'

const EMPTY: PostDraft = {
  title: '',
  date: new Date().toISOString().slice(0, 10),
  category: 'Technical',
  author: '',
  excerpt: '',
  body: '',
  faq: [],
}

const label = 'block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1.5'
const field =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'

/** Slug preview mirrors the server's `slugify` so the URL shown is the URL published. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export default function BlogEditorPage() {
  const { slug: editingSlug } = useParams<{ slug: string }>()
  const isEditing = Boolean(editingSlug)
  const navigate = useNavigate()

  const [draft, setDraft] = useState<PostDraft>(EMPTY)
  const [slugOverride, setSlugOverride] = useState('')
  const [expectedSha, setExpectedSha] = useState<string | undefined>()
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [validating, setValidating] = useState(false)
  const [configured, setConfigured] = useState(true)
  const [categories, setCategories] = useState<readonly BlogCategory[]>(BLOG_CATEGORIES)
  const [converting, setConverting] = useState(false)
  const [images, setImages] = useState<DraftImage[]>([])
  /** A cover picked in this session; committed on publish. */
  const [coverImage, setCoverImage] = useState<DraftImage | null>(null)
  /** The cover already in the repo, fetched so an edited post can show it. */
  const [committedCover, setCommittedCover] = useState<string | null>(null)
  const [coverStatus, setCoverStatus] = useState<'idle' | 'loading' | 'missing'>('idle')
  const [dropping, setDropping] = useState(false)
  /** Applied after the body re-renders; see the effect below. */
  const [pendingSelection, setPendingSelection] = useState<{ start: number; end: number } | null>(
    null
  )
  const [notes, setNotes] = useState<{ removed: string[]; warnings: string[] }>({
    removed: [],
    warnings: [],
  })

  const fileInput = useRef<HTMLInputElement>(null)
  const imageInput = useRef<HTMLInputElement>(null)
  const bodyInput = useRef<HTMLInputElement>(null)
  const bodyArea = useRef<HTMLTextAreaElement>(null)
  /** Which body image path the pending file picker will replace. */
  const replaceTarget = useRef<string | null>(null)

  const slug = slugOverride || slugify(draft.title)

  const set = useCallback(<K extends keyof PostDraft>(key: K, value: PostDraft[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }))
  }, [])

  useEffect(() => {
    blogApi
      .config()
      .then(result => {
        setConfigured(result.configured)
        if (result.categories?.length) setCategories(result.categories)
      })
      .catch(() => setConfigured(false))
  }, [])

  useEffect(() => {
    if (!editingSlug) return
    let cancelled = false
    blogApi
      .get(editingSlug)
      .then(result => {
        if (cancelled) return
        setExpectedSha(result.sha)
        if (result.post) {
          setDraft({
            title: result.post.title,
            date: result.post.date,
            category: result.post.category,
            author: result.post.author,
            excerpt: result.post.excerpt,
            body: result.post.body,
            faq: result.post.faq,
            cover: result.post.coverFile,
            coverAlt: result.post.coverAlt,
            featured: result.post.featured,
          })
          setSlugOverride(result.post.slug)
        } else {
          // The file exists but this parser cannot read it. Hand over the raw
          // markdown so it can be repaired here rather than only in an editor.
          toast.error(result.error ?? 'This post could not be parsed')
          setDraft(prev => ({ ...prev, body: result.raw }))
          setSlugOverride(editingSlug)
        }
      })
      .catch(error => toast.error(messageOf(error, 'Could not load the post')))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [editingSlug])

  // Cover art is compiled into the site rather than served from it, so there is
  // no URL to show it from — fetch it once. A 404 means the file is gone and the
  // next web build would fail on the unresolved import, so say so loudly. Runs
  // for an imported draft too: frontmatter can name a cover nobody committed.
  useEffect(() => {
    const filename = coverImage ? undefined : draft.cover
    if (!filename) return
    let cancelled = false
    setCoverStatus('loading')
    blogApi
      .cover(filename)
      .then(found => {
        if (cancelled) return
        setCommittedCover(found?.dataUri ?? null)
        setCoverStatus(found ? 'idle' : 'missing')
      })
      .catch(() => !cancelled && setCoverStatus('idle'))
    return () => {
      cancelled = true
    }
  }, [draft.cover, coverImage])

  // Moving the caret has to wait for the textarea to hold the new text, or the
  // range lands in the old, shorter body and gets clamped to its end.
  useEffect(() => {
    if (!pendingSelection) return
    const area = bodyArea.current
    if (area) {
      area.focus()
      area.setSelectionRange(pendingSelection.start, pendingSelection.end)
    }
    setPendingSelection(null)
  }, [pendingSelection])

  // Validate against the real build parser, debounced. This is what turns "the
  // release build failed" into an inline message while the author is still typing.
  useEffect(() => {
    if (!draft.title.trim() || !draft.body.trim()) {
      setValidation(null)
      return
    }
    setValidating(true)
    const timer = setTimeout(() => {
      blogApi
        .validate({ ...draft, slug: slug || undefined })
        .then(setValidation)
        .finally(() => setValidating(false))
    }, 500)
    return () => {
      clearTimeout(timer)
      setValidating(false)
    }
  }, [draft, slug])

  const applyFields = useCallback((fields: Partial<PostDraft>) => {
    setDraft(prev => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(fields).filter(([, value]) => value !== undefined && value !== '')
      ),
    }))
  }, [])

  /** Imports an already-formatted file verbatim. No model call, no images. */
  const importFormatted = useCallback(
    async (raw: string) => {
      // A Google Docs export down this path would dump a 375KB base64 payload
      // into the body, so send it where it belongs instead of mangling it.
      if (/data:image\/[a-z0-9.+-]+;base64,/i.test(raw)) {
        toast.error(
          'That file has images embedded in it, so it is a Google Docs export. Use "Upload Google Docs .md" at the top instead.'
        )
        return
      }
      try {
        const { data } = await api.post('/admin/blog/import', { raw })
        const fields = data.fields as Partial<PostDraft>
        applyFields(fields)
        if (fields.title && !slugOverride) setSlugOverride(slugify(fields.title))
        const unknown = data.unknownKeys as string[]
        if (unknown?.length) toast.warning(`Ignored unknown frontmatter: ${unknown.join(', ')}`)
        toast.success(
          fields.title ? 'Imported the draft and its frontmatter' : 'Imported the markdown body'
        )
      } catch (error) {
        toast.error(messageOf(error, 'Could not read that markdown'))
      }
    },
    [applyFields, slugOverride]
  )

  /**
   * The Google Docs pipeline: images extracted from the base64 payload in code,
   * the remaining prose cleaned up by a model, images reattached to the result.
   */
  const convertDraft = useCallback(
    async (raw: string) => {
      setConverting(true)
      try {
        const result = await blogApi.convert(raw, slugOverride || undefined)
        applyFields(result.fields)
        if (!slugOverride) setSlugOverride(result.slug)
        setImages(result.images)
        setNotes({ removed: result.removed, warnings: result.warnings })
        toast.success(
          result.images.length
            ? `Converted the draft and extracted ${result.images.length} image${result.images.length === 1 ? '' : 's'}`
            : 'Converted the draft'
        )
      } catch (error) {
        toast.error(messageOf(error, 'Could not convert that draft'))
      } finally {
        setConverting(false)
      }
    },
    [applyFields, slugOverride]
  )

  const onGoogleDocsFile = useCallback(
    async (file: File) => convertDraft(await file.text()),
    [convertDraft]
  )

  const onFormattedFile = useCallback(
    async (file?: File) => {
      if (!file) return
      await importFormatted(await file.text())
    },
    [importFormatted]
  )

  const faq = draft.faq ?? []
  const setFaq = (next: FaqItem[]) => set('faq', next)

  const errors = validation?.errors ?? []
  const canPublish =
    configured &&
    !saving &&
    coverStatus !== 'missing' &&
    Boolean(draft.title.trim() && draft.body.trim()) &&
    validation?.valid

  /** Strip the `data:image/png;base64,` prefix — the server commits raw payload. */
  const payload = (image: DraftImage) => ({
    filename: image.filename,
    base64: image.dataUri.replace(/^data:[^;]+;base64,/, ''),
  })

  const publish = async () => {
    setSaving(true)
    try {
      // Only images the body still points at: deleting a reference should not
      // leave the file behind in the repo.
      const used = referencedImages(draft.body)
      const result = await blogApi.save({
        ...draft,
        slug: slug || undefined,
        expectedSha,
        images: images.filter(image => used.has(image.url)).map(payload),
        coverImage: coverImage ? payload(coverImage) : undefined,
      })
      toast.success(result.message)
      navigate('/admin/blog')
    } catch (error) {
      toast.error(messageOf(error, 'Could not publish'))
    } finally {
      setSaving(false)
    }
  }

  const preview = useMemo(() => draft.body, [draft.body])
  const placeholderCount = useMemo(() => countAuthoringComments(draft.body), [draft.body])

  const resolveImage = useCallback(
    (src: string) => images.find(image => image.url === src)?.dataUri,
    [images]
  )

  const describeImage = useCallback(
    (src: string) => {
      const image = images.find(entry => entry.url === src)
      if (!image) return undefined
      return { text: describe(image, true), warn: Boolean(image.lowRes) }
    },
    [images]
  )

  /**
   * Swap one image for a better file. Works both for images just extracted from
   * a Google Docs export and for ones already committed to the repo — in the
   * latter case the entry is created here so the publish step overwrites it.
   */
  const applyReplacement = useCallback(
    async (file: File, targetUrl: string) => {
      try {
        const next = await buildReplacement(file, targetUrl)
        setImages(prev => {
          const without = prev.filter(image => image.url !== targetUrl)
          return [...without, next]
        })
        // A different extension means a different path, so the body reference
        // has to move with it or the post would point at the old file.
        if (replacementNeedsBodyRewrite(targetUrl, next)) {
          setDraft(prev => ({ ...prev, body: prev.body.split(targetUrl).join(next.url) }))
        }
        toast.success(
          next.lowRes
            ? `Replaced — but ${next.width}px is still narrower than the ${CONTENT_WIDTH_PX}px column.`
            : `Replaced with a ${next.width}×${next.height} image.`
        )
      } catch (error) {
        toast.error(
          error instanceof ImageReplaceError ? error.message : 'Could not use that image'
        )
      }
    },
    []
  )

  const requestReplace = useCallback((src: string) => {
    replaceTarget.current = src
    imageInput.current?.click()
  }, [])

  /**
   * Put one or more picked images into the body at `caret`.
   *
   * The file is named after the post and attached to the draft; the body gets a
   * normal Markdown reference to the path it will be committed at, so an
   * uploaded image is indistinguishable from an imported one from here on.
   */
  const insertImages = useCallback(
    async (files: File[], caret: number) => {
      const added: DraftImage[] = []
      // Names have to account for images added earlier in this same batch, and
      // for images already in the body of a post being edited.
      const taken = new Set([
        ...images.map(image => image.filename),
        ...[...referencedImages(bodyArea.current?.value ?? draft.body)].map(
          url => url.split('/').pop() as string
        ),
      ])

      for (const file of files) {
        try {
          const image = await buildUpload(file, slug, taken)
          taken.add(image.filename)
          added.push(image)
        } catch (error) {
          toast.error(
            error instanceof ImageReplaceError ? error.message : `Could not use ${file.name}`
          )
        }
      }
      if (!added.length) return

      setImages(prev => [...prev, ...added])

      // Read the body back off the textarea rather than from the closure:
      // reading and measuring the files was asynchronous, and anything typed in
      // the meantime is in the element but not yet in this callback's `draft`.
      let body = bodyArea.current?.value ?? draft.body
      let at = caret
      let selection: { start: number; end: number } | null = null
      for (const image of added) {
        const result = insertImageMarkdown(body, at, image, altFromFilename(image.filename))
        body = result.body
        at = result.end
        // One image: land the author on its alt text, the only part worth typing.
        if (added.length === 1) selection = { start: result.altStart, end: result.altEnd }
      }
      set('body', body)
      setPendingSelection(selection ?? { start: at, end: at })

      const soft = added.filter(image => image.lowRes)
      toast.success(
        added.length === 1
          ? `Added ${added[0].filename}${added[0].lowRes ? ` — but ${added[0].width}px is narrower than the ${CONTENT_WIDTH_PX}px column, so it will look soft.` : ''}`
          : `Added ${added.length} images${soft.length ? `, ${soft.length} narrower than the ${CONTENT_WIDTH_PX}px column` : ''}.`
      )
    },
    [draft.body, images, set, slug]
  )

  const pickCover = useCallback(
    async (file: File) => {
      try {
        const cover = await buildCover(file, slug)
        setCoverImage(cover)
        setCommittedCover(null)
        setCoverStatus('idle')
        set('cover', cover.filename)
      } catch (error) {
        toast.error(error instanceof ImageReplaceError ? error.message : 'Could not use that image')
      }
    },
    [set, slug]
  )

  const clearCover = useCallback(() => {
    setCoverImage(null)
    setCommittedCover(null)
    setCoverStatus('idle')
    setDraft(prev => ({ ...prev, cover: undefined, coverAlt: undefined }))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading post…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Shared by every "Replace image" control in the preview; the target path
          is held in a ref so one input serves all of them. */}
      <input
        ref={imageInput}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,image/avif"
        className="hidden"
        onChange={event => {
          const file = event.target.files?.[0]
          const target = replaceTarget.current
          if (file && target) void applyReplacement(file, target)
          replaceTarget.current = null
          event.target.value = ''
        }}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/blog')}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Posts
          </Button>
          <h1 className="text-xl font-semibold">{isEditing ? 'Edit post' : 'New post'}</h1>
        </div>
        <Button onClick={publish} disabled={!canPublish}>
          {saving ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-1.5 h-4 w-4" />
          )}
          {isEditing ? 'Save changes' : 'Publish'}
        </Button>
      </div>

      {!configured && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Blog publishing is not configured on the server, so nothing can be saved yet. An
            engineer needs to set <code className="font-mono">BLOG_GITHUB_TOKEN</code>.
          </span>
        </div>
      )}

      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        Publishing commits the post to the website repository. It appears on the live site with the
        next production release, not straight away.
      </div>

      {converting && (
        <div className="flex items-center gap-3 rounded-md border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span>
            Extracting images and cleaning up the draft… this takes a few seconds for a long post,
            and longer if the first model is unavailable and another has to pick it up.
          </span>
        </div>
      )}

      {(notes.removed.length > 0 || notes.warnings.length > 0 || images.length > 0) && (
        <div className="rounded-md border border-gray-200 bg-white p-3 text-sm">
          {images.length > 0 && (
            <div className="text-gray-700">
              <p>
                <strong>{images.length}</strong> image{images.length === 1 ? '' : 's'} attached to
                this post. They are committed when you publish.
              </p>
              <ul className="mt-1 space-y-0.5 text-xs text-gray-500">
                {images.map(image => (
                  <li key={image.filename}>
                    <code className="font-mono">{image.filename}</code>
                    {image.width ? ` — ${image.width}×${image.height}` : ''}
                    {` — ${Math.round(image.bytes / 1024)}KB`}
                    {/* Google Docs downsamples on export, so this is common and
                        the author can still fix it by supplying the original. */}
                    {image.lowRes && (
                      <span className="ml-1 text-amber-700">
                        · narrower than the 736px article column, so it will look soft. Ask an
                        engineer to swap in the original if it matters.
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* The AI deleting things silently is the risk here, so list it. */}
          {notes.removed.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Removed</p>
              <ul className="mt-1 list-disc pl-5 text-gray-600">
                {notes.removed.map((note, index) => (
                  <li key={index}>{note}</li>
                ))}
              </ul>
            </div>
          )}
          {notes.warnings.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-amber-800">
              {notes.warnings.map((note, index) => (
                <li key={index}>{note}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!isEditing && <ImportInstructions onFile={onGoogleDocsFile} busy={converting} />}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold">Post details</h2>
              <div>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".md,.markdown,text/markdown"
                  className="hidden"
                  onChange={event => {
                    void onFormattedFile(event.target.files?.[0])
                    event.target.value = ''
                  }}
                />
                {/* Verbatim import for a file already in the blog's format. The
                    Google Docs pipeline lives in the instructions panel above. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInput.current?.click()}
                  title="For a file already in the blog's format. Google Docs exports go through the button at the top."
                >
                  <FileUp className="mr-1.5 h-4 w-4" /> Upload formatted .md
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className={label}>Title</label>
                <input
                  className={field}
                  value={draft.title}
                  onChange={event => set('title', event.target.value)}
                  placeholder="How to Build an AI Agent"
                />
              </div>

              <div>
                <label className={label}>URL</label>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-gray-400">/blog/</span>
                  <input
                    className={field}
                    value={slugOverride}
                    onChange={event => setSlugOverride(slugify(event.target.value))}
                    placeholder={slugify(draft.title) || 'auto-from-title'}
                  />
                </div>
                {isEditing && (
                  <p className="mt-1 text-xs text-amber-700">
                    Changing this publishes a new page and leaves the old URL behind.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Category</label>
                  <select
                    className={field}
                    value={draft.category}
                    onChange={event => set('category', event.target.value as BlogCategory)}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={label}>Date</label>
                  <input
                    type="date"
                    className={field}
                    value={draft.date}
                    onChange={event => set('date', event.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className={label}>Author</label>
                <input
                  className={field}
                  value={draft.author}
                  onChange={event => set('author', event.target.value)}
                  placeholder="Author name"
                />
              </div>

              <div>
                <label className={label}>Excerpt</label>
                <textarea
                  className={`${field} min-h-18 resize-y`}
                  value={draft.excerpt}
                  onChange={event => set('excerpt', event.target.value)}
                  placeholder="One or two lines shown under the title and on the index card."
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(draft.featured)}
                  onChange={event => set('featured', event.target.checked)}
                />
                Feature this post at the top of the blog index
              </label>
            </div>
          </div>

          <CoverImage
            image={coverImage}
            committedUri={committedCover}
            filename={draft.cover}
            alt={draft.coverAlt ?? ''}
            status={coverStatus}
            onPick={file => void pickCover(file)}
            onAlt={value => set('coverAlt', value)}
            onClear={clearCover}
            disabled={saving}
          />

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold">Content</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Markdown</span>
                {/* Shared by the button, the drop target and paste — all three
                    end in the same place, so one input serves them. */}
                <input
                  ref={bodyInput}
                  type="file"
                  accept={ACCEPT_IMAGES}
                  multiple
                  className="hidden"
                  onChange={event => {
                    const files = [...(event.target.files ?? [])]
                    const caret = bodyArea.current?.selectionStart ?? draft.body.length
                    if (files.length) void insertImages(files, caret)
                    event.target.value = ''
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bodyInput.current?.click()}
                  title="Insert an image where the cursor is. You can also drop one onto the editor or paste it."
                >
                  <ImagePlus className="mr-1.5 h-4 w-4" /> Add image
                </Button>
              </div>
            </div>
            <textarea
              ref={bodyArea}
              className={`${field} min-h-105 resize-y font-mono text-[13px] leading-relaxed ${
                dropping ? 'border-gray-900 ring-1 ring-gray-900' : ''
              }`}
              value={draft.body}
              onChange={event => set('body', event.target.value)}
              onDragOver={event => {
                if (!isFileDrag(event.dataTransfer)) return
                event.preventDefault()
                setDropping(true)
              }}
              onDragLeave={() => setDropping(false)}
              onDrop={event => {
                setDropping(false)
                if (!isFileDrag(event.dataTransfer)) return
                // Before the early return below: an unprevented file drop makes
                // the browser navigate to the file, taking the draft with it.
                event.preventDefault()
                const files = imageFilesFrom(event.dataTransfer)
                if (!files.length) {
                  toast.error('Only images can be dropped into the editor.')
                  return
                }
                // Drop position, not the last caret position: dropping a file
                // does not move the caret the way dragging text does.
                const area = event.currentTarget
                void insertImages(files, caretFromPoint(area, event.clientX, event.clientY))
              }}
              onPaste={event => {
                const files = imageFilesFrom(event.clipboardData)
                if (files.length) {
                  event.preventDefault()
                  void insertImages(files, event.currentTarget.selectionStart)
                  return
                }
                const text = event.clipboardData.getData('text/plain')
                // A paste that carries its own frontmatter is a whole draft, not
                // body text: split it into the form instead of dumping it here.
                if (!draft.body.trim() && /^---\r?\n/.test(text)) {
                  event.preventDefault()
                  void importFormatted(text)
                }
              }}
              placeholder={
                'Paste the whole draft here — if it starts with a --- frontmatter block, the fields above fill in automatically.\n\n## A heading\n\nSome prose.'
              }
            />
            <p className="mt-2 text-xs text-gray-500">
              Add an image with the button, or drop or paste one straight into the editor — it lands
              where the cursor is and uploads to{' '}
              <code className="font-mono">public/blog-images/</code> when you publish. To move it,
              move its{' '}
              <code className="font-mono">![Caption](/blog-images/file.png)</code> line.
            </p>
          </div>

          <ImageAltText
            body={draft.body}
            resolveImage={resolveImage}
            onChange={(index, alt) => set('body', setImageAlt(draft.body, index, alt))}
          />

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold">FAQ</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFaq([...faq, { question: '', answer: '' }])}
              >
                <Plus className="mr-1.5 h-4 w-4" /> Add
              </Button>
            </div>
            {faq.length === 0 && (
              <p className="text-sm text-gray-400">
                No FAQ entries. These render as an accordion under the post.
              </p>
            )}
            <div className="space-y-3">
              {faq.map((item, index) => (
                <div key={index} className="rounded-md border border-gray-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">Question {index + 1}</span>
                    <button
                      className="text-gray-400 hover:text-red-600"
                      onClick={() => setFaq(faq.filter((_, i) => i !== index))}
                      aria-label={`Remove question ${index + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <input
                    className={`${field} mb-2`}
                    value={item.question}
                    placeholder="What is an AI agent?"
                    onChange={event =>
                      setFaq(
                        faq.map((entry, i) =>
                          i === index ? { ...entry, question: event.target.value } : entry
                        )
                      )
                    }
                  />
                  <textarea
                    className={`${field} min-h-16 resize-y`}
                    value={item.answer}
                    placeholder="Answer"
                    onChange={event =>
                      setFaq(
                        faq.map((entry, i) =>
                          i === index ? { ...entry, answer: event.target.value } : entry
                        )
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold">Checks</h2>
            {validating && (
              <p className="flex items-center text-sm text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking…
              </p>
            )}
            {!validating && validation?.valid && (
              <div className="space-y-1 text-sm">
                <p className="flex items-center text-green-700">
                  <CheckCircle2 className="mr-2 h-4 w-4" /> Ready to publish
                </p>
                <p className="text-gray-500">
                  <span className="font-mono">{validation.path}</span> ·{' '}
                  {validation.readingMinutes} min read · {validation.toc?.length ?? 0} sections
                </p>
              </div>
            )}
            {/* Not a parser error, so it cannot come back in `errors` — but it
                stops publishing, so it belongs next to the ones that do. */}
            {coverStatus === 'missing' && (
              <p className="flex items-start text-sm text-red-700">
                <AlertTriangle className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <span className="font-medium">cover: </span>
                  <code className="font-mono">{draft.cover}</code> is not in the repository. Upload a
                  cover or remove it.
                </span>
              </p>
            )}
            {!validating && errors.length > 0 && (
              <ul className="space-y-1 text-sm text-red-700">
                {errors.map((error, index) => (
                  <li key={index} className="flex items-start">
                    <AlertTriangle className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      {error.field && <span className="font-medium">{error.field}: </span>}
                      {error.message}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {!validating && !validation && (
              <p className="text-sm text-gray-400">Add a title and some content to run checks.</p>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold">Preview</h2>
            {placeholderCount > 0 && (
              <p className="mb-3 rounded border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500">
                {placeholderCount} image {placeholderCount === 1 ? 'placeholder' : 'placeholders'} in
                this post. They are notes for an engineer and do not appear on the published page.
              </p>
            )}
            <div className="max-w-none text-left">
              {preview.trim() ? (
                <PreviewMarkdown
                  body={preview}
                  resolveImage={resolveImage}
                  describeImage={describeImage}
                  onReplaceImage={requestReplace}
                />
              ) : (
                <p className="text-sm text-gray-400">Nothing to preview yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
